import { useEffect, useState } from 'react';
import { X, Zap, Briefcase } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SrLookup } from './SrLookup';
import { useJobs } from '../hooks/useApi';

interface CommandPaletteProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export function CommandPalette({ isOpen: controlledIsOpen, onClose: controlledOnClose }: CommandPaletteProps = {}) {
  const [internalOpen, setInternalOpen] = useState(false);

  // Support both controlled (isOpen/onClose props) and uncontrolled (internal state) modes
  const isControlled = controlledIsOpen !== undefined;
  const open = isControlled ? controlledIsOpen : internalOpen;
  const handleClose = isControlled
    ? (controlledOnClose ?? (() => {}))
    : () => setInternalOpen(false);

  const navigate = useNavigate();
  const { data: jobsData } = useJobs({ status: 'active' });
  const activeJobs = jobsData?.jobs?.slice(0, 3) ?? [];

  useEffect(() => {
    if (isControlled) return; // skip keyboard handling when controlled
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setInternalOpen((o) => !o);
      } else if (e.key === 'Escape' && internalOpen) {
        setInternalOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [internalOpen, isControlled]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center pt-32"
      onClick={handleClose}
    >
      <div onClick={(e) => e.stopPropagation()} className="relative">
        {/* AI ACTIONS */}
        <div className="px-3 pt-3 pb-1">
          <p className="text-[10px] font-semibold text-stone-400 dark:text-stone-500 uppercase tracking-wider mb-1">AI ACTIONS</p>
          <div className="space-y-0.5">
            <button
              onClick={() => { navigate('/applications?status=enriched'); handleClose(); }}
              className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-stone-100 dark:hover:bg-gray-700 text-left group"
            >
              <span className="w-6 h-6 flex items-center justify-center bg-amber-100 dark:bg-amber-900/40 rounded text-amber-600 dark:text-amber-400 flex-shrink-0">
                <Zap size={12} />
              </span>
              <div>
                <p className="text-sm font-medium text-stone-800 dark:text-stone-100">Force AI review — all pending</p>
                <p className="text-xs text-stone-400 dark:text-stone-500">Navigate to pending applications</p>
              </div>
              <span className="ml-auto text-stone-300 dark:text-stone-600 group-hover:text-stone-400">›</span>
            </button>
          </div>
        </div>

        {/* JUMP TO JOB */}
        {activeJobs.length > 0 && (
          <div className="px-3 pt-2 pb-1">
            <p className="text-[10px] font-semibold text-stone-400 dark:text-stone-500 uppercase tracking-wider mb-1">JUMP TO JOB</p>
            <div className="space-y-0.5">
              {activeJobs.map((job) => (
                <button
                  key={job.id}
                  onClick={() => { navigate(`/jobs/${job.id}`); handleClose(); }}
                  className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-stone-100 dark:hover:bg-gray-700 text-left group"
                >
                  <span className="w-6 h-6 flex items-center justify-center bg-stone-100 dark:bg-gray-700 rounded text-stone-500 dark:text-stone-400 flex-shrink-0">
                    <Briefcase size={12} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-stone-800 dark:text-stone-100 truncate">{job.title}</p>
                    {job.department && (
                      <p className="text-xs text-stone-400 dark:text-stone-500 truncate">{job.department}</p>
                    )}
                  </div>
                  <span className="ml-auto text-stone-300 dark:text-stone-600 group-hover:text-stone-400 flex-shrink-0">›</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Divider before SR lookup */}
        <div className="border-t border-stone-100 dark:border-gray-700 my-1" />

        <SrLookup variant="palette" onNavigated={handleClose} />
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-white"
          aria-label="Close palette"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
